import {
  calculateBlockedUntilDate,
  calculateEnforcementEndDate,
} from '@exam-center/blocked-user-management';
import { BlockedRecordBlockedScope, type AddBlockedRecordInput } from '@exam-center/graphql';
import { dayjs } from '@shared/utils';

import { type BlockedUserCreationFormType } from '../../providers';

import { markUndefinedIfEmptyString } from './utils';

export function mapBlockedStaffUserCreationFormToRequestPayload(
  formData: BlockedUserCreationFormType,
): AddBlockedRecordInput {
  const { personal, blocklistInfo } = formData;

  if (
    !personal.idNumber ||
    !personal.title ||
    !personal.firstName ||
    !personal.lastName ||
    !personal.email
  ) {
    throw new Error('Personal information is required.');
  }

  if (
    !blocklistInfo.blockedType ||
    !blocklistInfo.blockedAt ||
    !blocklistInfo.blockedCourses?.type ||
    !blocklistInfo.blockedType?.value
  ) {
    throw new Error('Blocklist information is required.');
  }

  const isAssignEnforcementEnd = blocklistInfo.enforcementEnd?.assign;

  let enforcementEndDate: Date | null = null;
  let blockedUntil: Date | null = null;

  if (isAssignEnforcementEnd) {
    enforcementEndDate = calculateEnforcementEndDate({
      blockedAt: blocklistInfo.blockedAt ?? null,
      enforcementEndDuration: blocklistInfo.enforcementEnd?.duration?.value ?? null,
      enforcementEndDate: blocklistInfo.enforcementEnd?.date ?? null,
    });

    blockedUntil = calculateBlockedUntilDate({
      blockedType: blocklistInfo.blockedType.value,
      enforcementEndDate,
    });
  }

  const examCourseCodes =
    blocklistInfo.blockedCourses?.type === BlockedRecordBlockedScope.PartialScope
      ? blocklistInfo.blockedCourses?.courses?.map((course) => course.value)
      : undefined;

  const idNumber = personal.idNumber.replace(/-/g, '');
  const phoneNumber = personal.phoneNumber?.replace(/-/g, '');

  return {
    idType: personal.idType,
    idNumber,
    title: personal.title.value,
    firstName: personal.firstName,
    middleName: markUndefinedIfEmptyString(personal.middleName),
    lastName: personal.lastName,
    phoneNumber: markUndefinedIfEmptyString(phoneNumber),
    email: personal.email,
    blockedType: blocklistInfo.blockedType.value,
    blockedScope: blocklistInfo.blockedCourses.type,
    examCenterCode: blocklistInfo.examCenter?.value,
    examCourseCodes,
    blockedAt: blocklistInfo.blockedAt,
    blockedUntil,
    blockedReason: markUndefinedIfEmptyString(blocklistInfo.reason),
    metadata: {
      position: markUndefinedIfEmptyString(blocklistInfo.position),
      department: markUndefinedIfEmptyString(blocklistInfo.department),
      enforcementEndDate,
    },
  };
}

export function mapBlockedCheaterUserCreationFormToRequestPayload(
  formData: BlockedUserCreationFormType,
): AddBlockedRecordInput {
  const { personal, blocklistInfo } = formData;

  if (
    !personal.idNumber ||
    !personal.title ||
    !personal.firstName ||
    !personal.lastName ||
    !personal.email
  ) {
    throw new Error('Personal information is required.');
  }

  if (
    !blocklistInfo.blockedType ||
    !blocklistInfo.blockedAt ||
    !blocklistInfo.blockedCourses?.type ||
    !blocklistInfo.blockedType?.value ||
    !blocklistInfo.enforcementEnd?.date ||
    !blocklistInfo.examCenter?.value ||
    !blocklistInfo.examSessionInfo?.endedAt ||
    !blocklistInfo.examSessionInfo?.startedAt ||
    !blocklistInfo.examSessionInfo?.round ||
    !blocklistInfo.examSessionInfo?.examDate
  ) {
    throw new Error('Blocklist information is required.');
  }

  const blockedUntil = blocklistInfo.enforcementEnd.date;

  const examCourseCodes =
    blocklistInfo.blockedCourses?.type === BlockedRecordBlockedScope.PartialScope
      ? blocklistInfo.blockedCourses?.courses?.map((course) => course.value)
      : undefined;

  const idNumber = personal.idNumber.replace(/-/g, '');
  const phoneNumber = personal.phoneNumber?.replace(/-/g, '');

  const startedAtWithExamDate = dayjs(blocklistInfo.examSessionInfo.examDate)
    .set('hour', dayjs(blocklistInfo.examSessionInfo.startedAt).hour())
    .set('minute', dayjs(blocklistInfo.examSessionInfo.startedAt).minute())
    .toDate();

  const endedAtWithExamDate = dayjs(blocklistInfo.examSessionInfo.examDate)
    .set('hour', dayjs(blocklistInfo.examSessionInfo.endedAt).hour())
    .set('minute', dayjs(blocklistInfo.examSessionInfo.endedAt).minute())
    .toDate();

  return {
    idType: personal.idType,
    idNumber,
    title: personal.title.value,
    firstName: personal.firstName,
    middleName: markUndefinedIfEmptyString(personal.middleName),
    lastName: personal.lastName,
    phoneNumber: markUndefinedIfEmptyString(phoneNumber),
    email: personal.email,
    blockedType: blocklistInfo.blockedType.value,
    blockedScope: blocklistInfo.blockedCourses.type,
    examCourseCodes,
    examCenterCode: blocklistInfo.examCenter.value,
    blockedAt: blocklistInfo.blockedAt,
    blockedUntil,
    blockedReason: markUndefinedIfEmptyString(blocklistInfo.reason),
    metadata: {
      examSession: {
        endedAt: endedAtWithExamDate,
        startedAt: startedAtWithExamDate,
        roundNumber: Number(blocklistInfo.examSessionInfo.round),
      },
    },
  };
}
